import type { SiteProfile, ScoreResult, WeatherData } from "../types";
import { COMPONENT_LABELS } from "../types";
import { colors, riskColors, fonts, scoreToColor } from "../tokens";
import { ScoreRing } from "./ScoreRing";

export interface ProjectScore {
  result: ScoreResult;
  weather: WeatherData;
  wxSource: string;
}

interface Props {
  profiles: SiteProfile[];
  scores: (ProjectScore | null)[];
  onSelect: (idx: number) => void;
  role: "gc" | "sub";
}

export function Dashboard({ profiles, scores, onSelect, role }: Props) {
  const scored = scores.filter(Boolean) as ProjectScore[];
  const totalWorkers = profiles.reduce((s, p) => s + p.workforce.total_workers, 0);
  const totalSIF = scored.reduce((s, ps) => s + ps.result.sif.length, 0);
  const levelCounts = scored.reduce(
    (acc, ps) => {
      acc[ps.result.level] = (acc[ps.result.level] || 0) + 1;
      return acc;
    },
    {} as Record<string, number>,
  );

  return (
    <div>
      {/* Project Cards */}
      <div
        className="dashboard-grid"
        style={{
          display: "grid",
          gridTemplateColumns: `repeat(${profiles.length}, 1fr)`,
          gap: 14,
        }}
      >
        {profiles.map((p, i) => {
          const ps = scores[i];
          return (
            <button
              key={p.project_id}
              onClick={() => onSelect(i)}
              style={{
                background: colors.surface,
                border: `1px solid ${colors.border}`,
                borderRadius: 8,
                padding: 0,
                cursor: "pointer",
                textAlign: "left",
                color: colors.text,
                overflow: "hidden",
                transition: "border-color 0.15s ease",
              }}
            >
              {/* Risk level accent bar */}
              <div
                style={{
                  height: 3,
                  background: ps ? riskColors[ps.result.level] : colors.border,
                }}
              />

              <div style={{ padding: "16px 20px" }}>
                {/* Project header */}
                <div style={{ marginBottom: 14 }}>
                  <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 2 }}>
                    {p.project_name}
                  </div>
                  <div style={{ fontSize: 11, color: colors.muted }}>{p.location_name}</div>
                </div>

                {ps ? (
                  <>
                    {/* Score ring + top 3 component bars */}
                    <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
                      <ScoreRing score={ps.result.overall} level={ps.result.level} size={80} />
                      <div style={{ flex: 1 }}>
                        {Object.entries(ps.result.scores)
                          .sort(([, a], [, b]) => b - a)
                          .slice(0, 3)
                          .map(([k, v]) => (
                            <div
                              key={k}
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 6,
                                marginBottom: 4,
                              }}
                            >
                              <span
                                style={{
                                  fontSize: 9,
                                  color: colors.muted,
                                  fontFamily: fonts.mono,
                                  width: 60,
                                  textAlign: "right",
                                  letterSpacing: "0.03em",
                                }}
                              >
                                {COMPONENT_LABELS[k] ?? k}
                              </span>
                              <div
                                style={{
                                  flex: 1,
                                  height: 3,
                                  background: colors.border,
                                  borderRadius: 1,
                                  overflow: "hidden",
                                }}
                              >
                                <div
                                  style={{
                                    height: "100%",
                                    borderRadius: 1,
                                    width: `${v}%`,
                                    background: scoreToColor(v),
                                    transition: "width 0.6s ease",
                                  }}
                                />
                              </div>
                            </div>
                          ))}
                      </div>
                    </div>

                    {/* SIF flags summary */}
                    {ps.result.sif.length > 0 && (
                      <div
                        style={{
                          marginTop: 12,
                          padding: "6px 8px",
                          background: colors.redGlow,
                          borderRadius: 4,
                          border: `1px solid ${colors.red}22`,
                        }}
                      >
                        <span
                          style={{
                            fontSize: 9,
                            fontFamily: fonts.mono,
                            color: colors.red,
                            fontWeight: 700,
                            letterSpacing: "0.06em",
                          }}
                        >
                          ⚠ {ps.result.sif.length} SIF-POTENTIAL
                        </span>
                      </div>
                    )}

                    {/* Weather + workers summary */}
                    <div
                      style={{
                        marginTop: 12,
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        paddingTop: 10,
                        borderTop: `1px solid ${colors.border}`,
                      }}
                    >
                      <span
                        style={{ fontSize: 10, color: colors.muted, fontFamily: fonts.mono }}
                      >
                        {ps.weather.temperature_f}°F · {ps.weather.wind_speed_mph}mph
                      </span>
                      <span
                        style={{ fontSize: 10, color: colors.muted, fontFamily: fonts.mono }}
                      >
                        {p.workforce.total_workers} workers
                      </span>
                    </div>

                    {/* View details prompt */}
                    <div style={{ marginTop: 10, textAlign: "right" }}>
                      <span
                        style={{
                          fontSize: 10,
                          fontFamily: fonts.mono,
                          color: colors.amber,
                          letterSpacing: "0.06em",
                        }}
                      >
                        {role === "gc" ? "VIEW DETAILS" : "DAILY CHECK-IN"} →
                      </span>
                    </div>
                  </>
                ) : (
                  <div style={{ textAlign: "center", padding: 24 }}>
                    <span
                      style={{
                        fontFamily: fonts.mono,
                        fontSize: 10,
                        color: colors.muted,
                        letterSpacing: "0.1em",
                        animation: "pulse 1.5s infinite",
                      }}
                    >
                      SCORING...
                    </span>
                  </div>
                )}
              </div>
            </button>
          );
        })}
      </div>

      {/* Portfolio Summary */}
      {scored.length > 0 && (
        <div
          style={{
            marginTop: 16,
            padding: "12px 16px",
            background: colors.surface,
            border: `1px solid ${colors.border}`,
            borderRadius: 6,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 8,
          }}
        >
          <div style={{ display: "flex", gap: 14, alignItems: "center", flexWrap: "wrap" }}>
            <span
              style={{
                fontFamily: fonts.mono,
                fontSize: 8,
                letterSpacing: "0.12em",
                color: colors.muted,
                textTransform: "uppercase",
              }}
            >
              Portfolio
            </span>
            <span style={{ fontSize: 11, color: colors.text2 }}>
              {profiles.length} active projects
            </span>
            {Object.entries(levelCounts).map(([level, count]) => (
              <span
                key={level}
                style={{
                  fontSize: 9,
                  fontFamily: fonts.mono,
                  padding: "2px 8px",
                  borderRadius: 3,
                  background: `${riskColors[level]}18`,
                  color: riskColors[level],
                  letterSpacing: "0.06em",
                }}
              >
                {count} {level.toUpperCase()}
              </span>
            ))}
          </div>
          <div style={{ display: "flex", gap: 16, alignItems: "center" }}>
            <span style={{ fontSize: 11, color: colors.text2, fontFamily: fonts.mono }}>
              {totalWorkers} total workers
            </span>
            {totalSIF > 0 && (
              <span style={{ fontSize: 11, color: colors.red, fontFamily: fonts.mono }}>
                {totalSIF} SIF flags
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
